/**
 * The password policy — and an honest statement of what it is worth.
 *
 * ── this is UX, not a control ───────────────────────────────────────────────
 *
 * Say it here rather than let a reader infer otherwise from the amount of code:
 * **nothing in this file is a security boundary.** It runs in the browser, and
 * an attacker setting a password is not running our JavaScript — they are
 * calling `PUT /auth/v1/user` directly. The server-side authority is GoTrue's
 * own `GOTRUE_PASSWORD_MIN_LENGTH` (and, on newer builds,
 * `GOTRUE_PASSWORD_REQUIRED_CHARACTERS`), which is deployment configuration.
 *
 * What this file IS for: telling a real person, before they commit to it, that
 * the password they just typed is their email address with a 1 on the end. That
 * is worth doing well, and it is worth doing WITHOUT pretending it is a gate.
 *
 * The pairing matters and is easy to get half-right: a client-side meter with no
 * server minimum is theatre, and a server minimum with no client feedback is a
 * form that rejects you after you press the button. `dist/.env.example` carries
 * the server half; this is the other one.
 *
 * ── why these rules and not the usual ones ──────────────────────────────────
 *
 * NIST SP 800-63B is explicit that composition rules (one upper, one digit, one
 * symbol) make passwords WORSE: people satisfy them predictably — `Password1!`
 * — and the entropy gained is less than the entropy lost to the pattern. It
 * asks instead for length, and for a check against known-bad values. So:
 *
 *   · LENGTH is the only strength rule. 12 is the floor. NIST's own floor is 8;
 *     12 is where an offline attack against a modern hash stops being trivial,
 *     and it is not so long that people write it on a card.
 *   · 72 BYTES is the ceiling, and it is not arbitrary: GoTrue hashes with
 *     bcrypt, and bcrypt silently ignores everything past the 72nd BYTE. A form
 *     that accepts a 200-character passphrase is telling the user something
 *     false about how strong it is — and worse, a passphrase truncated at 72
 *     bytes can collide with a shorter one the user never chose. Bytes rather
 *     than characters because that is what bcrypt counts, and a portal serving
 *     `é` and `日` must not be off by a factor of three.
 *   · KNOWN-BAD is checked against the address being reset and against a small
 *     list of the passwords that actually appear at the top of every breach
 *     corpus. A full k-anonymity check against Have I Been Pwned would be
 *     better and is deliberately NOT done here: it is an egress call to a third
 *     party from a sovereign platform's login screen, which is a decision for
 *     the deployment (docs/content-security-policy.md, egress-policy.ts), not
 *     something a component gets to make. Tracked as a suggestion in the PR.
 *   · No rotation, no history, no "must differ from the last 5". They are all
 *     things NIST withdrew, and none of them is expressible client-side anyway.
 */

/** The floor. Below this the form refuses; above it, longer is the only advice. */
export const MIN_LENGTH = 12

/**
 * The ceiling, in BYTES — bcrypt ignores everything after this, so accepting
 * more would overstate the strength of what was actually stored.
 */
export const MAX_BYTES = 72

/**
 * The short list.
 *
 * Deliberately short. A 100k-entry list shipped in the login bundle costs every
 * visitor the download and catches barely more than this does: the long tail of
 * a breach corpus is dominated by values a 12-character minimum already
 * excludes. These are the ones that clear 12 characters and still appear near
 * the top — plus the keyboard walks people reach for when a form demands length.
 */
const KNOWN_BAD = [
  'password', 'passw0rd', 'password1', 'password123', 'password1234',
  'qwertyuiop', 'qwerty123456', '1qaz2wsx3edc', 'zaq12wsxcde3',
  '123456789012', '1234567890123', '112233445566', '123123123123',
  'iloveyou123', 'letmein12345', 'welcome12345', 'admin1234567',
  'trustno1234', 'monkey123456', 'football1234', 'baseball1234',
  'sunshine1234', 'princess1234', 'dragon123456', 'superman1234',
  'qazwsxedcrfv', 'asdfghjkl123', 'abcdefghijkl', 'aaaaaaaaaaaa',
]

/** One problem with the password, in the words the person needs to fix it. */
export interface PasswordIssue {
  /** Stable, so a test names a rule rather than matching prose. */
  code: 'too_short' | 'too_long' | 'known_bad' | 'contains_identity' | 'repetitive'
  message: string
}

export interface PasswordContext {
  /** The address the reset is for — the single most common bad password. */
  email?: string | null
  /**
   * Anything else that must not BE the password: the portal's name, the
   * organisation's name. Passed as data so this file never learns a brand.
   */
  avoid?: (string | null | undefined)[]
}

/** Bytes, not characters — see MAX_BYTES. */
export function byteLength(s: string): number {
  // TextEncoder is in every browser we support and in Node ≥11. The fallback
  // keeps a bare test context (or a very old embedded webview) from throwing on
  // the login screen of all places.
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length
  return unescape(encodeURIComponent(s)).length
}

/** Strip anything that is not a letter or a digit, and fold case. */
function fold(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * A run of one character, or a straight ascending/descending run, covering most
 * of the value. `aaaaaaaaaaaa` and `abcdefghijkl` both clear 12 characters and
 * neither is a password.
 */
function isRepetitive(pw: string): boolean {
  if (pw.length < MIN_LENGTH) return false
  const chars = [...pw.toLowerCase()]
  if (new Set(chars).size <= 2) return true

  let runs = 1
  let longest = 1
  for (let i = 1; i < chars.length; i++) {
    const d = chars[i]!.charCodeAt(0) - chars[i - 1]!.charCodeAt(0)
    if (d === 1 || d === -1 || d === 0) { runs++; longest = Math.max(longest, runs) } else { runs = 1 }
  }
  // Two thirds of the value being one straight run is a keyboard drag, not a
  // choice. Not the whole value: `passwordabcd` should fail on KNOWN_BAD terms,
  // and a passphrase with `... deed ...` in it must not trip this.
  return longest >= Math.ceil(chars.length * 0.66)
}

/**
 * Every reason this password is refused, in the order a person should fix them.
 * An empty array means acceptable — never a boolean, so the form can say WHY.
 */
export function passwordIssues(password: string, ctx: PasswordContext = {}): PasswordIssue[] {
  const issues: PasswordIssue[] = []

  if (password.length < MIN_LENGTH) {
    issues.push({
      code: 'too_short',
      message: `Use at least ${MIN_LENGTH} characters. A few ordinary words in a row is stronger than a short one with symbols in it.`,
    })
  }

  if (byteLength(password) > MAX_BYTES) {
    issues.push({
      code: 'too_long',
      message: `That is longer than ${MAX_BYTES} bytes, which is as much as the password store actually uses — shorten it so what you type is what protects you.`,
    })
  }

  const folded = fold(password)

  // The address, and its local part on its own — `christopher@moonbase.be` and
  // `christopher` are the same bad idea.
  const identity = [ctx.email, ctx.email?.split('@')[0], ...(ctx.avoid ?? [])]
    .map((v) => (v ? fold(v) : ''))
    // 3 rather than 0: a portal called "AB" would otherwise ban every password
    // containing `ab`, which is most of them.
    .filter((v) => v.length >= 3)

  if (folded && identity.some((v) => folded.includes(v) || v.includes(folded))) {
    issues.push({
      code: 'contains_identity',
      message: 'Do not use your email address or the name of this portal — those are the first things anyone tries.',
    })
  }

  if (folded && KNOWN_BAD.some((bad) => folded === bad || folded.includes(bad))) {
    issues.push({
      code: 'known_bad',
      message: 'That password appears in public breach lists. Pick something that is not on one.',
    })
  }

  if (isRepetitive(password)) {
    issues.push({
      code: 'repetitive',
      message: 'That is one repeated or sequential run — it is long but not hard to guess.',
    })
  }

  return issues
}

/** True when `passwordIssues` is empty. A convenience, never the authority. */
export function isAcceptablePassword(password: string, ctx: PasswordContext = {}): boolean {
  return passwordIssues(password, ctx).length === 0
}

/**
 * A coarse 0–4 for the meter.
 *
 * Deliberately coarse and deliberately length-driven: a precise-looking score
 * from a client-side heuristic invites people to believe it. It answers "is this
 * in the shape of a decent password", nothing more, and any refused password
 * scores 0 regardless of length so the meter and the message never disagree.
 */
export function passwordScore(password: string, ctx: PasswordContext = {}): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0
  if (passwordIssues(password, ctx).length > 0) return 0
  const variety = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(password)).length
  if (password.length >= 20) return 4
  if (password.length >= 16) return variety >= 2 ? 4 : 3
  return variety >= 3 ? 3 : 2
}
